import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import SavedTherapiesTable from '../components/SavedTherapiesTable';
import SavedTherapiesModal from '../components/modals/SavedTherapiesModal';
import { fetchSavedTherapies } from '../api/savedTherapiesApi';
import { userPickupMethodOptions } from '../../users/constants/userConstants';
import useTableSearch from '../../../shared/hooks/useTableSearch';
import MedicationFilterSelect, {
  DEFAULT_MEDICATION_MATCH,
} from '../../medications/components/MedicationFilterSelect';
import { PAGE_SIZE } from '../../../config';
import TotalCountField from '../../../components/elements/TotalCountField';

const emptyState = {
  icon: '/assets/icons/no-users.svg',
  title: 'Trenutno nema sacuvanih terapija',
};

export default function SavedTherapiesPage() {
  // Jedinstvena pretraga (Pacijent/LBO/BZK) - src/config/searchConfig.js.
  const tableSearch = useTableSearch('savedTherapies');
  const { reset: resetTableSearch } = tableSearch;
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [medicationMatch, setMedicationMatch] = useState(
    DEFAULT_MEDICATION_MATCH
  );
  const [startDateSearch, setStartDateSearch] = useState('');
  const [endDateSearch, setEndDateSearch] = useState('');
  const [filter, setFilter] = useState({});

  const [patients, setPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [totalSaved, setTotalSaved] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);

  const latestRequestRef = useRef(0);

  const {
    userSearch: debouncedUserSearch,
    lboSearch: debouncedLboSearch,
    bzkSearch: debouncedBzkSearch,
  } = tableSearch.params;

  const pickupMethodFilter = useMemo(
    () => filter.pickupMethod || null,
    [filter.pickupMethod]
  );
  const pickupMethodFilterKey = useMemo(
    () => (pickupMethodFilter ? pickupMethodFilter.join(',') : ''),
    [pickupMethodFilter]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedUserSearch,
    debouncedLboSearch,
    debouncedBzkSearch,
    selectedMedications,
    medicationMatch,
    startDateSearch,
    endDateSearch,
  ]);

  const loadSavedTherapies = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setListLoading(true);

    const {
      data,
      ok,
      message: responseMessage,
    } = await fetchSavedTherapies({
      page: currentPage,
      limit: PAGE_SIZE,
      userSearch: debouncedUserSearch || null,
      lboSearch: debouncedLboSearch || null,
      bzkSearch: debouncedBzkSearch || null,
      medications: selectedMedications,
      medicationMatch,
      pickupMethod: pickupMethodFilterKey
        ? pickupMethodFilterKey.split(',')
        : null,
      startDate: startDateSearch || null,
      endDate: endDateSearch || null,
    });

    if (requestId !== latestRequestRef.current) return;

    setListLoading(false);

    if (!ok) {
      setMessage(
        responseMessage ||
          'Doslo je do greske pri dobavljanju sacuvanih terapija.'
      );
      return;
    }

    setMessage('');

    const rows = data?.patients || data?.data?.patients || [];
    const pagination = data?.pagination || data?.data?.pagination || {};

    setPatients(rows);
    setTotalPages(pagination?.totalPages || 1);
    setTotalResults(pagination?.totalCount ?? null);
    setTotalSaved(
      data?.totalSavedOccurrences ?? data?.data?.totalSavedOccurrences ?? null
    );

    const resolvedPage = pagination?.currentPage || 1;
    if (resolvedPage !== currentPage) {
      setCurrentPage(resolvedPage);
    }
  }, [
    currentPage,
    debouncedBzkSearch,
    debouncedLboSearch,
    debouncedUserSearch,
    endDateSearch,
    pickupMethodFilterKey,
    selectedMedications,
    medicationMatch,
    startDateSearch,
  ]);

  useEffect(() => {
    loadSavedTherapies();
  }, [loadSavedTherapies]);

  // Modal sa detaljima drzi selektovanog pacijenta iz poslednjeg ucitavanja liste.
  useEffect(() => {
    if (!isPatientModalOpen || !selectedPatient?.userId) return;

    const refreshedPatient = patients.find(
      (patient) => patient?.userId === selectedPatient.userId
    );

    if (refreshedPatient && refreshedPatient !== selectedPatient) {
      setSelectedPatient(refreshedPatient);
    }
  }, [isPatientModalOpen, patients, selectedPatient]);

  const pickupMethodFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'pickupMethod',
        buttonLabel: 'Preuzimanje',
        multiSelect: true,
        items: userPickupMethodOptions.map((option) => ({
          key: option.value,
          label: option.label,
        })),
      },
    ],
    []
  );

  const handleFilterChange = (newFilter) => {
    setCurrentPage(1);
    setFilter((prev) => ({ ...prev, ...newFilter }));
  };

  const hasActiveFilters = Boolean(
    tableSearch.isActive ||
      selectedMedications.length ||
      startDateSearch ||
      endDateSearch ||
      pickupMethodFilter
  );

  const handleResetFilters = () => {
    resetTableSearch();
    setSelectedMedications([]);
    setMedicationMatch(DEFAULT_MEDICATION_MATCH);
    setStartDateSearch('');
    setEndDateSearch('');
    setFilter({});
    setCurrentPage(1);
  };

  const handleDateRangeChange = useCallback((startDate, endDate) => {
    setStartDateSearch(startDate);
    setEndDateSearch(endDate);
  }, []);

  const dateFilter = useMemo(
    () => ({
      type: 'dateRange',
      value: [startDateSearch, endDateSearch],
      placeholder: ['Datum od', 'Datum do'],
      onChange: handleDateRangeChange,
      className: 'therapyFilterDate',
    }),
    [endDateSearch, handleDateRangeChange, startDateSearch]
  );

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setIsPatientModalOpen(true);
  };

  const handlePatientModalClose = () => {
    setIsPatientModalOpen(false);
    setSelectedPatient(null);
  };

  return (
    <>
      <ContentLayout>
        {!!message && (
          <Alert
            title={message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <TableSection
          // Za loader tabele (da li jos nema redova).
          data={patients}
          showFilter
          showCreateButton={false}
          searchFieldOrder={[
            'tableSearch',
            'filter',
            'tertiarySearch',
            'quaternarySearch',
          ]}
          inlineHeaderActions={
            hasActiveFilters ? (
              <Button
                type="default"
                className="btn-reset-filters"
                onClick={handleResetFilters}
              >
                <span className="btn-reset-filters__content">
                  Ponisti filtere
                  <CloseOutlined />
                </span>
              </Button>
            ) : null
          }
          rightHeaderActions={
            <TotalCountField
              label="Ukupno sačuvanih terapija:"
              count={totalSaved}
              loading={listLoading}
            />
          }
          tableSearch={tableSearch}
          tertiarySearch={{
            type: 'custom',
            value: selectedMedications,
            onChange: setSelectedMedications,
            className: 'therapyFilterMedication',
            render: () => (
              <MedicationFilterSelect
                value={selectedMedications}
                onChange={setSelectedMedications}
                match={medicationMatch}
                onMatchChange={setMedicationMatch}
              />
            ),
          }}
          quaternarySearch={dateFilter}
          filter={{
            selected: filter,
            sections: [pickupMethodFilterSections],
            popupClassName: 'pickup-method-filter-dropdown',
            layoutClassName: 'therapyFilterPickup',
          }}
          onFilterChange={handleFilterChange}
          customTable={
            <SavedTherapiesTable
              data={patients}
              onPatientClick={handlePatientClick}
              emptyState={emptyState}
            />
          }
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
        />
      </ContentLayout>
      <SavedTherapiesModal
        isOpen={isPatientModalOpen}
        onClose={handlePatientModalClose}
        patient={selectedPatient}
      />
    </>
  );
}
